import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Hero.css';
import heroIllustration from '../assets/images/hero-illustration.png.png';
import powerText from '../assets/images/power-text.png.png';
import greenBg from '../assets/images/Green.png';

function Hero({ onScrollProgress }) {
  const heroRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const powerTextRef = useRef(null);
  const animationProgress = useRef(0);
  const isLocked = useRef(false);
  const touchStartY = useRef(0);

  const updateAnimation = useCallback((progress) => {
    animationProgress.current = progress;

    if (progress <= 0) {
      setVisible(false);
      if (powerTextRef.current) {
        powerTextRef.current.style.transform = 'scale(1) translate(0, 0)';
        powerTextRef.current.style.opacity = '1';
      }
      onScrollProgress(0);
    } else if (progress < 1) {
      setVisible(false);
      const scale = 1 - progress * 0.95;
      const translateY = -progress * 120;
      const translateX = -progress * 80;

      if (powerTextRef.current) {
        powerTextRef.current.style.transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`;
        powerTextRef.current.style.opacity = `${1 - progress}`;
      }
      onScrollProgress(progress);
    } else {
      setVisible(true);
      onScrollProgress(1);
    }
  }, [onScrollProgress]);

  useEffect(() => {
    const isMobile = window.innerWidth <= 480;
    const totalDelta = isMobile ? 150 : 300; // Total wheel delta needed to complete animation
    let accumulatedDelta = 0;
    // Track whether we're in the "reversing" phase (scroll-up animation lock)
    let isReversing = false;

    const lockScroll = () => {
      if (!isLocked.current) {
        isLocked.current = true;
        document.body.style.overflow = 'hidden';
      }
    };

    const unlockScroll = () => {
      if (isLocked.current) {
        isLocked.current = false;
        document.body.style.overflow = '';
      }
    };

    const handleWheel = (e) => {
      const atTop = window.scrollY <= 0;

      // CASE 1: At top, animation not done, scrolling down → drive animation forward
      if (atTop && animationProgress.current < 1 && e.deltaY > 0 && !isReversing) {
        e.preventDefault();
        lockScroll();
        accumulatedDelta += e.deltaY;
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress >= 1) {
          unlockScroll();
        }
      }
      // CASE 2: At top, animation complete, scrolling up → start reversing
      else if (atTop && animationProgress.current >= 1 && e.deltaY < 0) {
        e.preventDefault();
        lockScroll();
        isReversing = true;
        accumulatedDelta = Math.max(accumulatedDelta + e.deltaY, 0);
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress <= 0) {
          isReversing = false;
          // Keep locked — page is at top with power text showing, no need to scroll
        }
      }
      // CASE 3: Mid-animation (forward or reverse), consume all scroll
      else if (animationProgress.current > 0 && animationProgress.current < 1) {
        e.preventDefault();
        lockScroll();
        if (e.deltaY > 0) {
          isReversing = false;
          accumulatedDelta += e.deltaY;
        } else {
          isReversing = true;
          accumulatedDelta = Math.max(accumulatedDelta + e.deltaY, 0);
        }
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress >= 1) {
          isReversing = false;
          unlockScroll();
        } else if (progress <= 0) {
          isReversing = false;
          // Stay locked at top with power text
        }
      }
      // CASE 4: Animation at 0 (power text fully visible), scrolling down → drive animation forward
      else if (atTop && animationProgress.current <= 0 && e.deltaY > 0) {
        e.preventDefault();
        lockScroll();
        isReversing = false;
        accumulatedDelta += e.deltaY;
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress >= 1) {
          unlockScroll();
        }
      }
      // CASE 5: Animation at 0, scrolling up → do nothing (already at start)
      else if (atTop && animationProgress.current <= 0 && e.deltaY < 0) {
        // Already fully reversed, nothing to do
      }
    };

    const handleTouchStart = (e) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const touchDelta = touchStartY.current - e.touches[0].clientY;
      touchStartY.current = e.touches[0].clientY;
      const atTop = window.scrollY <= 0;

      // CASE 1: At top, animation not done, swiping up → drive animation forward
      if (atTop && animationProgress.current < 1 && touchDelta > 0 && !isReversing) {
        e.preventDefault();
        lockScroll();
        accumulatedDelta += touchDelta * 2;
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress >= 1) {
          unlockScroll();
        }
      }
      // CASE 2: At top, animation complete, swiping down → start reversing
      else if (atTop && animationProgress.current >= 1 && touchDelta < 0) {
        e.preventDefault();
        lockScroll();
        isReversing = true;
        accumulatedDelta = Math.max(accumulatedDelta + touchDelta * 2, 0);
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress <= 0) {
          isReversing = false;
        }
      }
      // CASE 3: Mid-animation, consume all touch
      else if (animationProgress.current > 0 && animationProgress.current < 1) {
        e.preventDefault();
        lockScroll();
        if (touchDelta > 0) {
          isReversing = false;
          accumulatedDelta += touchDelta * 2;
        } else {
          isReversing = true;
          accumulatedDelta = Math.max(accumulatedDelta + touchDelta * 2, 0);
        }
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress >= 1) {
          isReversing = false;
          unlockScroll();
        } else if (progress <= 0) {
          isReversing = false;
        }
      }
      // CASE 4: Animation at 0, swiping up → drive forward
      else if (atTop && animationProgress.current <= 0 && touchDelta > 0) {
        e.preventDefault();
        lockScroll();
        isReversing = false;
        accumulatedDelta += touchDelta * 2;
        const progress = Math.min(accumulatedDelta / totalDelta, 1);
        updateAnimation(progress);

        if (progress >= 1) {
          unlockScroll();
        }
      }
    };

    // When user scrolls to top normally, prepare for reverse animation
    const handleScroll = () => {
      if (window.scrollY <= 0 && animationProgress.current >= 1 && !isLocked.current) {
        // User reached top via normal scroll — lock so next up-scroll reverses animation
        accumulatedDelta = totalDelta; // Set to full so reverse can decrement
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('scroll', handleScroll);
      unlockScroll();
    };
  }, [updateAnimation]);

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-content">
         <div className="hero-power-text" ref={powerTextRef} style={{ display: visible ? 'none' : 'block' }}>
          <img src={powerText} alt="power" />
        </div>
        {visible && (
          <div className='banner-text-box'>
            <h1 className='banner-text-heading'>Driving the green power transition.</h1>
            <p className='banner-text-description'>Vision is to support Gurugram and Nuh in becoming India's first green regions powered predominantly by clean energy and reduced diesel dependence.</p>
          </div>
        )}
        <div className="hero-illustration">
          <img src={heroIllustration} alt="Renewable energy landscape" />
        </div>
      </div>
      <div className="hero-tagline" style={{ backgroundImage: `url(${greenBg})` }}>
        <h1>Uninterrupted power.<br />Green energy. Consumer choice.</h1>
      </div>
    </section>
  );
}

export default Hero;
